import { useCallback } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { CreateEventFeature } from '@shared/constants/customPageMigration';
import { extractMigratedPageFeatures } from '@shared/constants/customPageMigration';

/**
 * Returns a function that computes the eventFeatures array based on
 * currently enabled feature flags. These features are passed to the
 * backend during event creation to configure the new event.
 */
export const useCreateEventFeatures = () => {
  const [isWebsiteDesignerV3Enabled] = useIsFeatureEnabled('websiteDesignerV3Enabled');
  const [isWebsiteAllPagesAsCustomPagesEnabled, , , websiteAllPagesAsCustomPagesPayload] = useIsFeatureEnabled('websiteAllPagesAsCustomPagesEnabled');
  const [isGuestPageContentRedesignEnabled] = useIsFeatureEnabled('guestPageContentRedesign');
  const [isCreateWeddingBypassWebsiteThemeCustomizationEnabled] = useIsFeatureEnabled('createWeddingBypassWebsiteThemeCustomization');

  const getEventFeatures = useCallback((): ReadonlyArray<CreateEventFeature> => {
    const features: Array<CreateEventFeature> = [];

    if (isWebsiteDesignerV3Enabled) {
      features.push('useWebsiteDesignerV3');
    }

    if (isCreateWeddingBypassWebsiteThemeCustomizationEnabled) {
      features.push('bypassWebsiteThemeCustomization');
    }

    // Only derive per-page event features when the top-level remote flag is enabled.
    if (isWebsiteAllPagesAsCustomPagesEnabled) {
      features.push(...extractMigratedPageFeatures(websiteAllPagesAsCustomPagesPayload, 'migratedPages'));
    }

    if (isGuestPageContentRedesignEnabled) {
      features.push('useGuestPageContentRedesign');
    }

    return features;
  }, [
    isWebsiteDesignerV3Enabled,
    isWebsiteAllPagesAsCustomPagesEnabled,
    websiteAllPagesAsCustomPagesPayload,
    isGuestPageContentRedesignEnabled,
    isCreateWeddingBypassWebsiteThemeCustomizationEnabled,
  ]);

  return { getEventFeatures };
};
